// The upgrade paywall as a real popup: a plan picker with prices that starts
// Stripe checkout — shown whenever a site/edit/rebuild limit is hit, instead of
// a bare "you've reached your limit" line. Degrades safely: while Stripe is
// dormant the checkout button says so rather than erroring.

import { useEffect, useState } from "react";

import { ApiError, api, type Entitlements, type PlanOption, type PlansResponse } from "../../lib/api";
import { friendlyError } from "../../lib/friendlyError";

export type UpgradeReason = "site" | "edit" | "rebuild";

const COPY: Record<UpgradeReason, { title: string; blurb: string; metric: (p: PlanOption) => number; unit: string }> = {
  site: {
    title: "You've reached your plan's site limit",
    blurb: "Upgrade to add another site — pick a plan below and you'll come right back to finish.",
    metric: (p) => p.siteLimit, unit: "sites",
  },
  rebuild: {
    title: "You've used all your redesigns",
    blurb: "A redesign rebuilds your whole site from scratch. Upgrade to keep going.",
    metric: (p) => p.rebuildLimit, unit: "redesigns",
  },
  edit: {
    title: "You've used all your edits",
    blurb: "Upgrade to keep making changes to your site this period.",
    metric: (p) => p.editLimit, unit: "edits",
  },
};

function limitText(n: number, unlimited: number): string {
  return n === unlimited ? "Unlimited" : n.toLocaleString();
}

// Bold + ink the limit the customer actually ran out of; the rest stay muted.
function Metric({ on, children }: { on: boolean; children: React.ReactNode }) {
  return on
    ? <strong style={{ color: "var(--ink, #172033)" }}>{children}</strong>
    : <>{children}</>;
}

export default function UpgradeModal({
  open, onClose, reason,
}: { open: boolean; onClose: () => void; reason: UpgradeReason }) {
  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setNote(null); setPlans(null);
    Promise.all([api.getPlans(), api.getEntitlements().catch(() => null)])
      .then(([p, e]) => { if (alive) { setPlans(p); setEnt(e); } })
      .catch(() => { if (alive) setNote("Couldn't load plans right now. Try again in a moment."); });
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  async function upgrade(planId: string) {
    setBusy(planId); setNote(null);
    try {
      const { url } = await api.startCheckout(planId);
      if (url) { window.location.href = url; return; }
      setNote("Checkout isn't open yet — we'll email you the moment billing goes live.");
    } catch (e) {
      setNote(e instanceof ApiError && e.status === 503
        ? "Billing isn't switched on yet — there's nothing to pay today."
        : friendlyError(e, "Couldn't start checkout. Try again in a moment."));
    } finally {
      setBusy(null);
    }
  }

  const copy = COPY[reason];
  const unlimited = plans?.unlimited ?? -1;
  const currentId = ent?.plan;
  // Offer the paid plans the account isn't already on. If we can tell what the
  // current plan grants, only show plans that actually raise the hit limit.
  const currentMetric = (() => {
    const cur = plans?.plans.find((p) => p.id === currentId);
    return cur ? copy.metric(cur) : -Infinity;
  })();
  const options = (plans?.plans ?? []).filter((p) =>
    p.priceUsd > 0 && p.id !== currentId &&
    (currentMetric === -Infinity || copy.metric(p) === unlimited || copy.metric(p) > currentMetric),
  );

  return (
    <div
      className="app-dialog-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-title"
        className="stack"
        style={{
          width: "min(560px, 94vw)", maxHeight: "90vh", overflowY: "auto",
          background: "var(--surface, #fff)", borderRadius: 18, padding: 26, gap: 14,
          boxShadow: "0 30px 80px -30px rgba(15,23,42,.45)", position: "relative",
        }}
      >
        <button
          type="button" aria-label="Close" onClick={onClose}
          style={{
            position: "absolute", top: 14, right: 14, width: 30, height: 30, borderRadius: 999,
            border: "1px solid var(--border)", background: "transparent", cursor: "pointer",
            fontSize: 16, lineHeight: 1, color: "var(--muted, #697386)",
          }}
        >
          ×
        </button>

        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--primary, #0593e6)" }}>
          Upgrade your plan
        </div>
        <h2 id="upgrade-title" style={{ margin: 0, fontSize: 20, letterSpacing: "-.01em" }}>{copy.title}</h2>
        <p className="muted" style={{ margin: 0, fontSize: 14, maxWidth: "46ch" }}>{copy.blurb}</p>

        {plans === null && !note && (
          <div className="muted" style={{ fontSize: 13, padding: "8px 0" }}>Loading plans…</div>
        )}

        {plans && options.length === 0 && (
          <div className="muted" style={{ fontSize: 13 }}>
            You're already on our top plan. <a href="mailto:dev@prototypical.ai">Talk to us</a> about more capacity.
          </div>
        )}

        <div className="stack" style={{ gap: 10 }}>
          {options.map((p, i) => (
            <div
              key={p.id}
              className="row between"
              style={{
                alignItems: "center", gap: 12, padding: 14, borderRadius: 12,
                border: `1px solid ${i === 0 ? "var(--primary, #0593e6)" : "var(--border)"}`,
                background: i === 0 ? "#eef8fe" : "var(--surface, #fff)",
              }}
            >
              <div className="stack" style={{ gap: 3 }}>
                <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
                  <strong style={{ fontSize: 15 }}>{p.name}</strong>
                  {i === 0 && <span className="badge good" style={{ fontSize: 11 }}>Recommended</span>}
                </div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  <Metric on={reason === "site"}>{limitText(p.siteLimit, unlimited)} sites</Metric>
                  {" · "}
                  <Metric on={reason === "edit"}>{limitText(p.editLimit, unlimited)} edits</Metric>
                  {" · "}
                  <Metric on={reason === "rebuild"}>{limitText(p.rebuildLimit, unlimited)} redesigns</Metric>
                </div>
              </div>
              <div className="stack" style={{ gap: 6, alignItems: "flex-end", flexShrink: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 700, whiteSpace: "nowrap" }}>
                  ${p.priceUsd}<span className="muted" style={{ fontSize: 12, fontWeight: 500 }}>/mo</span>
                </div>
                <button
                  className="btn primary" type="button" disabled={busy === p.id}
                  onClick={() => void upgrade(p.id)} style={{ whiteSpace: "nowrap" }}
                >
                  {busy === p.id ? "Starting…" : "Choose plan →"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {note && (
          <div className="badge warn" style={{ alignSelf: "flex-start", whiteSpace: "normal", lineHeight: 1.4 }}>
            {note}
          </div>
        )}

        <div className="row between" style={{ alignItems: "center", marginTop: 2 }}>
          <span className="muted" style={{ fontSize: 12 }}>We'll email you before any charge. No card stored here.</span>
          <button type="button" className="btn" onClick={onClose}>Maybe later</button>
        </div>
      </div>
    </div>
  );
}
